"use client"
import { useLocale, useTranslations } from "next-intl"
import { addDays, addMonths, format, startOfDay } from "date-fns"
import { getDateFnsLocale } from "@/lib/i18n/locale-utils"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { NumericInput } from "@/components/ui/numeric-input"
import { decimalCodec, integerCodec } from "@/lib/forms/numeric-draft"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog"
import type { StepProps, ListingFormat, ListingType } from "@/types/listing"
import { isOfferEnabledFormat } from "@kraahl/contract"
import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { motion } from "framer-motion"
import { ChevronRight, ChevronDown } from "lucide-react"
import { useMobileInputScroll } from "@/hooks/use-mobile-input-scroll"
import { DEFAULT_CURRENCY, getCurrencySymbol, isZeroDecimalCurrency } from "@/lib/currencies"
import { computeAuctionEndAt, formatAuctionEndCopy } from "@/lib/auction-timing"
import {
  creationFlowInputClass,
  creationFlowLabelClass,
  creationFlowBlockClass,
  creationFlowMutedClass,
  creationFlowTextareaClass,
  creationFlowSectionLabelClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"

export function DetailsStep({
  data,
  updateDataAction,
  onNextAction,
  hideButton,
  onValidationChange,
  onRegisterSave,
  compact,
  hideQuantity,
  hideListingFormatSections = false,
  showListingTypeSection: showListingTypeSectionProp,
  showSaleTypeSection: showSaleTypeSectionProp,
  commerceOnly = false,
  allowAuctionOption = false,
}: StepProps & {
  hideQuantity?: boolean
  hideListingFormatSections?: boolean
  showListingTypeSection?: boolean
  showSaleTypeSection?: boolean
  commerceOnly?: boolean
}) {
  const showListingTypeSection =
    showListingTypeSectionProp ?? (!commerceOnly && !hideListingFormatSections)
  const showSaleTypeSection =
    showSaleTypeSectionProp ?? (!hideListingFormatSections || commerceOnly)
  const persistCommerce = showListingTypeSection || showSaleTypeSection || commerceOnly
  const tLC = useTranslations("listingCreation");
  const tPO = useTranslations("preorder");
  const locale = useLocale();
  const dateLocale = getDateFnsLocale(locale);
  const [title, setTitle] = useState(data.title || "")
  const [description, setDescription] = useState(data.description || "")
  // `undefined` = the seller has not filled the box in. Previously `0` did
  // double duty as "empty" AND "none in stock", which meant a real 0 could
  // never be displayed; the two are now distinct.
  const [quantity, setQuantity] = useState<number | undefined>(hideQuantity ? (data.quantity ?? 1) : (data.quantity ?? undefined))
  // Numeric fields hold NUMBERS, not draft strings. `NumericInput` owns the
  // draft (partial entries like "6." live inside the input), so state here is
  // free of `parseFloat` at every read site. `undefined` = field cleared.
  const [price, setPrice] = useState<number | undefined>(data.price ?? undefined)
  const [listingFormat, setListingFormat] = useState<ListingFormat>(data.listing_format ?? "fixed_price")
  // Phase 3 (2026-06-29): the "accepts offers" UI toggle is now a function
  // of `listing_format` only. The boolean state is local UX (toggle on/off
  // while editing); on save we translate `fixed_price + toggle ON` into
  // `fixed_price_with_offers`. See isOfferEnabledFormat for the canonical
  // derivation.
  const [acceptsOffers, setAcceptsOffers] = useState<boolean>(
    isOfferEnabledFormat(data.listing_format ?? "fixed_price")
  )
  const [autoDeclineBelow, setAutoDeclineBelow] = useState<number | undefined>(
    data.auto_decline_below ?? undefined
  )
  const [auctionStartPrice, setAuctionStartPrice] = useState<number | undefined>(data.auction_start_price ?? undefined)
  const [auctionReservePrice, setAuctionReservePrice] = useState<number | undefined>(data.auction_reserve_price ?? undefined)
  const [listingType, setListingType] = useState<ListingType>(data.listing_type ?? "standard")
  const [preorderDeadline, setPreorderDeadline] = useState<string>(data.preorder_deadline ? data.preorder_deadline.slice(0, 10) : "")
  const [estimatedDelivery, setEstimatedDelivery] = useState<string>(data.estimated_delivery ? data.estimated_delivery.slice(0, 10) : "")
  const [minOrders, setMinOrders] = useState<number | undefined>(data.min_orders ?? undefined)
  const [maxOrders, setMaxOrders] = useState<number | undefined>(data.max_orders ?? undefined)
  const [preorderError, setPreorderError] = useState<string>("")
  // Auction allowlist check removed — auctions are now open to all users
  const [isAllowlistChecked] = useState(true)
  const [allowlistCanCreateAuction] = useState(true)
  const currencyCode = data.currency || DEFAULT_CURRENCY
  const currencySymbol = getCurrencySymbol(currencyCode)
  const zeroDecimalCurrency = isZeroDecimalCurrency(currencyCode)
  // Zero-decimal currencies (JPY, TWD) have no minor unit, so a decimal
  // separator is not just unhelpful — it is not a representable amount.
  const moneyCodec = useMemo(
    () => decimalCodec(zeroDecimalCurrency ? 0 : 2),
    [zeroDecimalCurrency],
  )
  const countCodec = useMemo(() => integerCodec(), [])
  const titleInputRef = useMobileInputScroll<HTMLInputElement>()
  const priceInputRef = useMobileInputScroll<HTMLInputElement>()
  const quantityInputRef = useMobileInputScroll<HTMLInputElement>()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const updateDataRef = useRef(updateDataAction)
  updateDataRef.current = updateDataAction

  const canCreateAuction = allowAuctionOption && allowlistCanCreateAuction
  const [showAuctionConfirm, setShowAuctionConfirm] = useState(false)

  const isAuction = listingFormat === "auction"
  const isOffersOnly = listingFormat === "offers_only"
  const isPreorder = listingType === "preorder"
  const showOffersSettings = isOffersOnly || (listingFormat === "fixed_price" && acceptsOffers) || listingFormat === "fixed_price_with_offers"
  const effectiveQuantity = hideQuantity || isAuction ? 1 : (quantity ?? 0)
  const auctionEndCopy = useMemo(() => {
    if (!isAuction) return null
    const publishAt = data.scheduled_publish_at ? new Date(data.scheduled_publish_at) : new Date()
    return formatAuctionEndCopy(computeAuctionEndAt(publishAt))
  }, [isAuction, data.scheduled_publish_at])

  const deadlineQuickDates = useMemo(() => {
    const now = startOfDay(new Date())
    return [
      { days: 7, date: addDays(now, 7) },
      { days: 14, date: addDays(now, 14) },
      { days: 30, date: addDays(now, 30) },
    ].map((q) => ({
      key: `${q.days}d`,
      label: format(q.date, "d MMM", { locale: dateLocale }),
      value: format(q.date, "yyyy-MM-dd"),
    }))
  }, [dateLocale])

  const etaQuickDates = useMemo(() => {
    const anchor = preorderDeadline
      ? startOfDay(new Date(preorderDeadline))
      : startOfDay(new Date())
    return [
      { months: 1, date: addMonths(anchor, 1) },
      { months: 3, date: addMonths(anchor, 3) },
      { months: 6, date: addMonths(anchor, 6) },
    ].map((q) => ({
      key: `${q.months}mo`,
      label: format(q.date, "d MMM yyyy", { locale: dateLocale }),
      value: format(q.date, "yyyy-MM-dd"),
    }))
  }, [dateLocale, preorderDeadline])

  useEffect(() => {
    setTitle(data.title || "")
    setDescription(data.description || "")
    setQuantity(hideQuantity ? (data.quantity ?? 1) : (data.quantity ?? undefined))
    setPrice(data.price ?? undefined)
    const format = (data.listing_format as ListingFormat) ?? "fixed_price"
    setListingFormat(format)
    // Phase 3 (2026-06-29): "offers enabled" derives purely from format
    // (no separate boolean stored).
    setAcceptsOffers(isOfferEnabledFormat(format))
    setAutoDeclineBelow(data.auto_decline_below ?? undefined)
    setAuctionStartPrice(data.auction_start_price ?? undefined)
    setAuctionReservePrice(data.auction_reserve_price ?? undefined)
  }, [data.title, data.description, data.quantity, data.price, data.listing_format, data.auto_decline_below, data.auction_start_price, data.auction_reserve_price, hideQuantity])

  useEffect(() => {
    if (data.listing_type === "preorder" || data.listing_type === "standard") {
      setListingType(data.listing_type)
    }
  }, [data.listing_type])

  /* Auction allowlist check removed — auctions are now open to all users
  useEffect(() => {
    if (!allowAuctionOption) {
      setAllowlistCanCreateAuction(false)
      setIsAllowlistChecked(true)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const response = await fetch("/api/dev-listing-allowlist?checkCurrentUser=true")
        if (!response.ok) return
        const payload = await response.json().catch(() => null)
        if (!cancelled) {
          setAllowlistCanCreateAuction(Boolean(payload?.canViewDev))
        }
      } catch {
        if (!cancelled) {
          setAllowlistCanCreateAuction(false)
        }
      } finally {
        if (!cancelled) {
          setIsAllowlistChecked(true)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [allowAuctionOption])
  */

  useEffect(() => {
    if (!canCreateAuction && listingFormat === "auction") {
      setListingFormat("fixed_price")
      setAuctionStartPrice(undefined)
      setAuctionReservePrice(undefined)
      updateDataRef.current({
        listing_format: "fixed_price",
        auction_start_price: undefined,
        auction_reserve_price: undefined,
      })
    }
  }, [canCreateAuction, listingFormat])

  useEffect(() => {
    if (isAuction && quantity !== 1) {
      setQuantity(1)
    }
  }, [isAuction, quantity])

  const updateDataCallback = useCallback(updateDataAction, [updateDataAction])

  const saveData = useCallback(() => {
    const updates: Parameters<typeof updateDataCallback>[0] = {
      title,
      description,
      quantity: effectiveQuantity,
    }
    if (persistCommerce) {
      updates.listing_format = listingFormat
      updates.listing_type = listingType
      if (listingType === "preorder") {
        updates.preorder_deadline = preorderDeadline ? new Date(preorderDeadline).toISOString() : null
        updates.estimated_delivery = estimatedDelivery ? new Date(estimatedDelivery).toISOString() : null
        updates.min_orders = minOrders ?? null
        updates.max_orders = maxOrders ?? null
      } else {
        updates.preorder_deadline = null
        updates.estimated_delivery = null
        updates.min_orders = null
        updates.max_orders = null
      }
    }
    // Phase 3 (2026-06-29): `accepts_offers` collapsed into `listing_format`.
    // The UI toggle drives the FORMAT downstream; consumers read
    // `isOfferEnabledFormat(listing_format)` for the predicate.
    if (listingFormat === "auction") {
      updates.price = undefined
      updates.auction_start_price = auctionStartPrice
      updates.auction_reserve_price = auctionReservePrice ?? null
      updates.auto_decline_below = null
    } else if (listingFormat === "offers_only") {
      updates.price = undefined
      updates.auction_start_price = undefined
      updates.auction_reserve_price = undefined
      updates.auto_decline_below = autoDeclineBelow ?? null
    } else {
      updates.price = price
      updates.auction_start_price = undefined
      updates.auction_reserve_price = undefined
      const wantOffers = acceptsOffers || listingFormat === "fixed_price_with_offers"
      updates.listing_format = wantOffers ? "fixed_price_with_offers" : "fixed_price"
      updates.auto_decline_below = wantOffers ? autoDeclineBelow ?? null : null
    }
    updateDataCallback(updates)
  }, [
    title,
    description,
    effectiveQuantity,
    price,
    listingFormat,
    acceptsOffers,
    autoDeclineBelow,
    auctionStartPrice,
    auctionReservePrice,
    listingType,
    preorderDeadline,
    estimatedDelivery,
    minOrders,
    maxOrders,
    updateDataCallback,
    persistCommerce,
  ])

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(saveData, 500)
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current) }
  }, [saveData])

  const handleNext = () => {
    saveData()
    onNextAction()
  }

  useEffect(() => {
    if (!isPreorder) {
      setPreorderError("")
      return
    }
    const deadlineTs = preorderDeadline ? new Date(preorderDeadline).getTime() : null
    const etaTs = estimatedDelivery ? new Date(estimatedDelivery).getTime() : null
    if (deadlineTs != null && etaTs != null && etaTs < deadlineTs) {
      setPreorderError(tPO("creation.etaBeforeDeadline"))
      return
    }
    const minN = minOrders ?? null
    const maxN = maxOrders ?? null
    if (minN != null && maxN != null && maxN <= minN) {
      setPreorderError(tPO("creation.maxLteMin"))
      return
    }
    setPreorderError("")
  }, [isPreorder, preorderDeadline, estimatedDelivery, minOrders, maxOrders, tPO])

  // Numeric-field predicates. Spelled out because the string-era versions
  // (`!String(price).trim()`, `price !== ""`) still COMPILE against a number
  // and silently invert: `String(undefined)` is `"undefined"`, which is not
  // blank. Explicit `=== undefined` is the only reading that survives.
  const isBlank = (n: number | undefined) => n === undefined
  const isNonPositive = (n: number | undefined) => n !== undefined && n <= 0
  // Auto-decline must sit strictly below the asking price. Only meaningful
  // once both are filled in.
  const autoDeclineAbovePrice =
    autoDeclineBelow !== undefined && price !== undefined && autoDeclineBelow >= price
  const offersSettingsInvalid =
    showOffersSettings && autoDeclineBelow !== undefined && (
      isOffersOnly ? isNonPositive(autoDeclineBelow) : autoDeclineAbovePrice
    )

  const isNextDisabled = commerceOnly
    ? (isAuction
        ? isBlank(auctionStartPrice) || isNonPositive(auctionStartPrice)
        : isOffersOnly
          ? false
          : isBlank(price) || isNonPositive(price))
      || Boolean(preorderError)
      || offersSettingsInvalid
    : (isAuction
    ? !title.trim() || isBlank(auctionStartPrice) || isNonPositive(auctionStartPrice)
    : isOffersOnly
      ? !title.trim()
      : !title.trim() || isBlank(price))
    || Boolean(preorderError)
    || offersSettingsInvalid

  // Notify parent about validation status
  useEffect(() => {
    onValidationChange?.(!isNextDisabled)
  }, [isNextDisabled, onValidationChange])

  // Register save function
  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => {
      onRegisterSave?.(null)
    }
  }, [saveData, onRegisterSave])

  // Remove auto-focus to prevent keyboard issues on mobile
  // The user can manually tap the input when ready

  // Handle textarea focus/blur to prevent viewport issues on mobile
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    let focusTimer: number | null = null
    let blurTimer: number | null = null

    const handleTextareaFocus = () => {
      // On mobile, ensure we scroll the textarea into view smoothly
      if (focusTimer !== null) window.clearTimeout(focusTimer)
      focusTimer = window.setTimeout(() => {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
    }

    const handleTextareaBlur = () => {
      // Minimal cleanup on blur - don't force scroll to top
      if (window.innerHeight < 600) { // Likely mobile with keyboard
        if (blurTimer !== null) window.clearTimeout(blurTimer)
        blurTimer = window.setTimeout(() => {
          // Just force a layout reflow without scrolling
          document.body.style.height = '100vh'
          document.body.style.height = ''
        }, 100)
      }
    }

    textarea.addEventListener('focus', handleTextareaFocus)
    textarea.addEventListener('blur', handleTextareaBlur)

    return () => {
      if (focusTimer !== null) window.clearTimeout(focusTimer)
      if (blurTimer !== null) window.clearTimeout(blurTimer)
      textarea.removeEventListener('focus', handleTextareaFocus)
      textarea.removeEventListener('blur', handleTextareaBlur)
    }
  }, [])

  const containerSpacing = compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"
  const buttonClasses = compact ? "w-full h-14 text-sm shadow-md rounded-full group" : "w-full h-16 text-base shadow-lg rounded-full group"
  const quantityButton = compact ? "h-10 w-10 text-base" : "h-12 w-12 text-lg"
  const quantityInput = compact ? "h-10 text-center mx-2 text-base" : "h-12 text-center mx-2 text-lg"
  const inputBase = creationFlowInputClass
  const blockBase = creationFlowBlockClass
  const blockPadding = compact ? "p-3" : "p-4"
  const blockRadius = "rounded-2xl"
  const mutedText = creationFlowMutedClass
  const labelClass = `block mb-2 ${creationFlowLabelClass}`
  const sectionLabel = creationFlowSectionLabelClass
  /*
    `text-base` (16px) is required to stop iOS from zooming into the input
    on focus; anything below 16px triggers the auto-zoom heuristic.
  */
  const nativeInputClass = cn(
    "w-full min-w-0 appearance-none text-base rounded-xl focus:outline-none focus:ring-2 focus:ring-ring",
    "px-3 py-3 bg-muted text-foreground focus:ring-muted-foreground/70 focus:ring-offset-2 focus:ring-offset-sheet dark:[color-scheme:dark]"
  )
  // The preorder count fields were bare `<input>`s styled entirely by
  // `nativeInputClass`; `NumericInput` renders through `Input`, which brings
  // `h-10` + a border. Cancel both so the row keeps its `py-3` height and
  // borderless look next to the date fields beside it.
  const nativeStyledNumericClass = cn(nativeInputClass, "h-auto border-0")

  return (
    <div className={containerSpacing}>
      <motion.div
        className={compact ? "space-y-4" : "space-y-5"}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {!commerceOnly && (
        <div className={cn(blockPadding, blockRadius, blockBase)}>
          <p className={cn(sectionLabel, "mb-3")}>{tLC("item")}</p>
          <div className="space-y-3">
            <div>
              <Input
                placeholder={tLC("titleField")}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputBase}
                maxLength={100}
                ref={titleInputRef}
              />
              <div className={cn("text-xs text-right mt-1", mutedText)}>{title.length}/100</div>
            </div>
            <div>
              <Textarea
                ref={textareaRef}
                placeholder={tLC("descriptionOptional")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={cn("min-h-[120px] max-h-[200px] overflow-y-auto resize-none", creationFlowTextareaClass)}
                maxLength={500}
                rows={4}
              />
              <div className={cn("text-xs text-right mt-1", mutedText)}>{description.length}/500</div>
            </div>
          </div>
        </div>
        )}

        {showListingTypeSection && (
            <div className={cn(blockPadding, blockRadius, blockBase)}>
              <p className={cn(sectionLabel, "mb-3")}>{tPO("creation.sectionTitle")}</p>
              <div>
                <RadioGroup
                  value={listingType}
                  onValueChange={(v) => {
                    const next = v as ListingType
                    setListingType(next)
                    if (next === "preorder" && listingFormat === "auction") {
                      setListingFormat("fixed_price")
                      setAuctionStartPrice(undefined)
                      setAuctionReservePrice(undefined)
                    }
                  }}
                  className="flex flex-wrap gap-x-4 gap-y-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="standard" id="type-standard" />
                    <Label htmlFor="type-standard" className="cursor-pointer font-normal text-foreground">{tPO("types.standard")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="preorder" id="type-preorder" />
                    <Label htmlFor="type-preorder" className="cursor-pointer font-normal text-foreground">{tPO("types.preorder")}</Label>
                  </div>
                </RadioGroup>
              </div>
              {isPreorder && (
                <p className={cn("text-xs mt-2", mutedText)}>{tPO("creation.helper")}</p>
              )}
            </div>
        )}

        {showSaleTypeSection && !isPreorder && (
              <div className={cn(blockPadding, blockRadius, blockBase)}>
                <p className={cn(sectionLabel, "mb-3")}>{tLC("saleType")}</p>
                <Label className="sr-only">{tLC("saleType")}</Label>
                <div>
                  <RadioGroup
                    value={listingFormat === "fixed_price_with_offers" ? "fixed_price" : listingFormat}
                    onValueChange={(v) => {
                      const nextFormat = v as ListingFormat
                      if (nextFormat === "auction" && listingFormat !== "auction") {
                        setShowAuctionConfirm(true)
                        return
                      }
                      setListingFormat(nextFormat)
                      if (nextFormat !== "fixed_price") setAcceptsOffers(false)
                    }}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="fixed_price" id="sale-fixed" />
                        <Label htmlFor="sale-fixed" className="cursor-pointer font-normal text-foreground">{tLC("fixedPrice")}</Label>
                      </div>
                      {(listingFormat === "fixed_price" || listingFormat === "fixed_price_with_offers") && (
                        <div
                          className="ml-6 mt-1 flex items-start justify-between gap-3 rounded-xl px-3 py-2 bg-muted/60"
                        >
                          <Label
                            htmlFor="accept-offers-toggle"
                            className="flex-1 cursor-pointer space-y-0.5 text-foreground"
                          >
                            <span className="text-sm font-medium block text-foreground">
                              {tLC("acceptOffersTitle")}
                            </span>
                            <span className={cn("text-xs font-normal block", mutedText)}>{tLC("acceptOffersDesc")}</span>
                          </Label>
                          <Switch
                            id="accept-offers-toggle"
                            checked={acceptsOffers}
                            onCheckedChange={setAcceptsOffers}
                            className="mt-0.5"
                          />
                        </div>
                      )}
                    </div>
                    <div className={cn("flex items-center gap-2", !canCreateAuction && "opacity-60 pointer-events-none")}>
                      <RadioGroupItem value="auction" id="sale-auction" disabled={!canCreateAuction} />
                      <Label
                        htmlFor="sale-auction"
                        className={cn(canCreateAuction ? "cursor-pointer font-normal" : "cursor-not-allowed font-normal", "text-foreground")}
                      >
                        {tLC("auction")}
                      </Label>
                      {!canCreateAuction && (
                        <span className={cn("text-sm", mutedText)}>
                          {allowAuctionOption
                            ? (isAllowlistChecked ? tLC("comingThisWeek") : tLC("checkingAccess"))
                            : tLC("comingThisWeek")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="offers_only" id="sale-offers" />
                      <Label htmlFor="sale-offers" className="cursor-pointer font-normal text-foreground">
                        {tLC("offers")}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
        )}

        {!isAuction && !isOffersOnly ? (
          <div className={cn(blockPadding, blockRadius, blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{tLC("price")}</p>
            <div className="flex items-center">
              <span className="text-lg mr-2 text-foreground">{currencySymbol}</span>
              <NumericInput
                ref={priceInputRef}
                codec={moneyCodec}
                placeholder={zeroDecimalCurrency ? "0" : "0.00"}
                value={price}
                onValueChange={setPrice}
                className={cn("h-12 rounded-3xl", inputBase)}
              />
            </div>
          </div>
        ) : isOffersOnly ? null : (
          <>
            <div className={cn(blockPadding, blockRadius, blockBase)}>
              <p className={cn(sectionLabel, "mb-3")}>{tLC("startingPrice")}</p>
              <div className="flex items-center">
                <span className="text-lg mr-2">{currencySymbol}</span>
                <NumericInput
                  codec={moneyCodec}
                  placeholder={zeroDecimalCurrency ? "0" : "0.00"}
                  value={auctionStartPrice}
                  onValueChange={setAuctionStartPrice}
                  className={cn("h-12 rounded-3xl", inputBase)}
                />
              </div>
            </div>
            <div className={cn(blockPadding, blockRadius, blockBase)}>
              <p className={cn(sectionLabel, "mb-3")}>{tLC("reservePrice")}</p>
              <div className="flex items-center">
                <span className="text-lg mr-2 text-foreground">{currencySymbol}</span>
                <NumericInput
                  codec={moneyCodec}
                  placeholder={zeroDecimalCurrency ? "0" : "0.00"}
                  value={auctionReservePrice}
                  onValueChange={setAuctionReservePrice}
                  className={cn("h-12 rounded-3xl", inputBase)}
                />
              </div>
              <p className={cn("text-xs mt-1", mutedText)}>{tLC("reserveHidden")}</p>
            </div>
            <Collapsible defaultOpen={false} className={compact ? "rounded-lg" : ""}>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium w-full py-1 text-muted-foreground hover:text-foreground">
                <ChevronDown className="h-4 w-4 transition-transform in-data-[state=open]:rotate-180" />
                {tLC("auctionRules")}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <p className={cn("text-xs", mutedText)}>
                  {tLC("auctionRulesDesc")}
                </p>
              </CollapsibleContent>
            </Collapsible>
            {auctionEndCopy && (
              <p className={cn("text-xs", mutedText)}>
                {tLC("ends")} {auctionEndCopy} {tLC("optimized")}
              </p>
            )}
          </>
        )}

        {showOffersSettings && !isAuction && !isPreorder && (
          <div className={cn(blockPadding, blockRadius, blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{tLC("autoDeclineBelowTitle")}</p>
            <div className="flex items-center">
              <span className="text-lg mr-2 text-foreground">{currencySymbol}</span>
              <NumericInput
                codec={moneyCodec}
                placeholder={tLC("autoDeclineBelowPlaceholder")}
                value={autoDeclineBelow}
                onValueChange={setAutoDeclineBelow}
                className={cn("h-12 rounded-3xl", inputBase)}
              />
            </div>
            <p className={cn("text-xs mt-2", mutedText)}>{tLC("autoDeclineBelowDesc")}</p>
            {!isOffersOnly && autoDeclineAbovePrice && (
              <p className="text-xs mt-1 text-amber-500">{tLC("autoDeclineBelowMustBeLower")}</p>
            )}
          </div>
        )}

        {!hideQuantity && !isAuction ? (
          <div className={cn(blockPadding, blockRadius, blockBase)}>
            <p className={cn(sectionLabel, "mb-3")}>{tLC("quantity")}</p>
            <div className="flex items-center">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantity(Math.max(0, (quantity ?? 0) - 1))}
                className={cn(quantityButton, "bg-muted text-foreground hover:bg-sheet-handle border-0")}
              >
                -
              </Button>
              <NumericInput
                ref={quantityInputRef}
                codec={countCodec}
                value={quantity}
                placeholder="0"
                onValueChange={setQuantity}
                onFocus={(e) => {
                  if ((quantity ?? 0) > 0) {
                    e.target.select()
                  }
                }}
                className={cn(quantityInput, "caret-transparent", inputBase)}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setQuantity((quantity ?? 0) + 1)}
                className={cn(quantityButton, "bg-muted text-foreground hover:bg-sheet-handle border-0")}
              >
                +
              </Button>
            </div>
          </div>
        ) : null}

        {isPreorder && showListingTypeSection && (
          <div className={cn(blockPadding, blockRadius, blockBase, "space-y-4")}>
            <p className={cn(sectionLabel, "mb-1")}>{tPO("creation.fieldsTitle")}</p>

            <div className="space-y-3">
              <div>
                <Label className={labelClass} htmlFor="preorder-deadline">{tPO("creation.deadline")}</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {deadlineQuickDates.map((q) => {
                    const isActive = preorderDeadline === q.value
                    return (
                      <Button
                        key={q.key}
                        type="button"
                        size="sm"
                        variant={isActive ? "default" : "outline-solid"}
                        onClick={() => setPreorderDeadline(q.value)}
                        className={cn(
                          "rounded-full text-xs border-0",
                          !isActive && "bg-muted text-foreground hover:bg-sheet-handle",
                          isActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                        )}
                      >
                        {q.label}
                      </Button>
                    )
                  })}
                </div>
                <input
                  id="preorder-deadline"
                  type="date"
                  value={preorderDeadline}
                  onChange={(e) => setPreorderDeadline(e.target.value)}
                  min={format(startOfDay(new Date()), "yyyy-MM-dd")}
                  className={nativeInputClass}
                />
              </div>

              <div>
                <Label className={labelClass} htmlFor="preorder-eta">{tPO("creation.eta")}</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {etaQuickDates.map((q) => {
                    const isActive = estimatedDelivery === q.value
                    return (
                      <Button
                        key={q.key}
                        type="button"
                        size="sm"
                        variant={isActive ? "default" : "outline-solid"}
                        onClick={() => setEstimatedDelivery(q.value)}
                        className={cn(
                          "rounded-full text-xs border-0",
                          !isActive && "bg-muted text-foreground hover:bg-sheet-handle",
                          isActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                        )}
                      >
                        {q.label}
                      </Button>
                    )
                  })}
                </div>
                <input
                  id="preorder-eta"
                  type="date"
                  value={estimatedDelivery}
                  onChange={(e) => setEstimatedDelivery(e.target.value)}
                  min={preorderDeadline || format(startOfDay(new Date()), "yyyy-MM-dd")}
                  className={nativeInputClass}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className={labelClass} htmlFor="preorder-min">{tPO("creation.minOrders")}</Label>
                <NumericInput
                  id="preorder-min"
                  codec={countCodec}
                  placeholder={tPO("creation.minOrdersPlaceholder")}
                  value={minOrders}
                  onValueChange={setMinOrders}
                  className={nativeStyledNumericClass}
                />
              </div>
              <div>
                <Label className={labelClass} htmlFor="preorder-max">{tPO("creation.maxOrders")}</Label>
                <NumericInput
                  id="preorder-max"
                  codec={countCodec}
                  placeholder={tPO("creation.maxOrdersPlaceholder")}
                  value={maxOrders}
                  onValueChange={setMaxOrders}
                  className={nativeStyledNumericClass}
                />
              </div>
            </div>

            {preorderError && (
              <p className="text-xs text-red-500">{preorderError}</p>
            )}
            <p className={cn("text-xs", mutedText)}>{tPO("creation.fieldsHelper")}</p>
          </div>
        )}
      </motion.div>

      {!hideButton && (
        <div className="pt-6">
          <Button
            onClick={handleNext}
            disabled={!!isNextDisabled}
            className={buttonClasses}
            size={compact ? "default" : "lg"}
          >
            {tLC("continue")}
            <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}

      <AlertDialog open={showAuctionConfirm} onOpenChange={setShowAuctionConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tLC("auctionConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{tLC("auctionConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tLC("auctionConfirmCancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setListingFormat("auction")
                setQuantity(1)
                updateDataCallback({ quantity: 1 })
              }}
            >
              {tLC("auctionConfirmOk")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
